import React, { useState, useEffect, useMemo, useContext } from "react";
import cogoToast from "cogo-toast";
import { useParams, useHistory, useLocation } from "react-router-dom";
import {
  FaExclamationCircle,
  FaTimesCircle,
  FaCheckCircle,
  FaUser,
  FaCalendarAlt,
  FaCalendarDay,
  FaFileAlt,
  FaUserFriends,
  FaClipboardCheck,
  FaCommentDots,
} from "react-icons/fa";
import { PulseLoader } from "react-spinners";
import { genericGet, genericPost } from "../../../services/Api";
import { SUCCESS_RESULT, ENDPOINTS } from "../../../Constants";
import AppContext from "../../../AppContext";
import {
  getLoggedInUserId,
  isSameUserId,
  normalizeLeaveRecord,
} from "../../../services/Utils";
import LeaveDocumentList from "./LeaveDocumentList";
import "./ApproveLeave.css";

const getInitials = (name) => {
  if (!name || typeof name !== "string") return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

const Field = ({ label, value, highlight }) => (
  <div className="approve-leave-field">
    <div className="approve-leave-field__label">{label}</div>
    <div className={`approve-leave-field__value${highlight ? " approve-leave-field__value--highlight" : ""}`}>
      {value}
    </div>
  </div>
);

const SectionTitle = ({ icon: Icon, children }) => (
  <h2 className="approve-leave-section__title">
    <Icon /> {children}
  </h2>
);

function ApproveLeave() {
  const global = useContext(AppContext);
  const [approvalComments, setapprovalComments] = useState("");
  const { id } = useParams();
  const history = useHistory();
  const location = useLocation();
  const [is_loading, setIsLoading] = useState(false);
  const [rowData, setRowData] = useState(null);

  const currentUserId = getLoggedInUserId(global.get_logged_in_user());
  const isSupervisorApprover = isSameUserId(currentUserId, rowData?.supervisor_id);
  const isHrApprover = isSameUserId(currentUserId, rowData?.hr_id);
  const canTakeAction = isSupervisorApprover || isHrApprover;

  const leaveDocuments = useMemo(
    () => (Array.isArray(rowData?.leave_documents) ? rowData.leave_documents : []),
    [rowData]
  );

  const staffName = rowData?.staff?.staff_name || "—";
  const leaveTypeName = rowData?.leave_type?.name || "Leave request";

  useEffect(() => {
    fetch_data();
    return () => {};
  }, [id]);

  const approveLeave = (data) => {
    setIsLoading(true);
    genericPost(ENDPOINTS.APPROVE_LEAVE, data)
      .then((response) => {
        setIsLoading(false);
        if (response.status == 200) {
          if (response.data.status == SUCCESS_RESULT) {
            cogoToast.success(response.data.message, { hideAfter: 5 });
            history.push({ pathname: "/hrm/leave/pending-actions" });
          } else {
            cogoToast.error(response.data.message, { hideAfter: 5 });
          }
        }
      })
      .catch((err) => {
        setIsLoading(false);
        console.log(err);
        cogoToast.error("Something went wrong, please Try again. " + err, {
          hideAfter: 5,
        });
      });
  };

  const fetch_data = () => {
    if (!id) return;

    setIsLoading(true);
    genericGet(`${ENDPOINTS.LEAVE_DETAILS}/${id}`)
      .then((res) => {
        setIsLoading(false);
        if (res.status == 200) {
          if (res.data.status == SUCCESS_RESULT) {
            const normalized = normalizeLeaveRecord(
              res.data.data,
              location?.state?.data
            );
            setRowData(normalized);
          } else {
            cogoToast.error(res.data.message, {});
          }
        }
      })
      .catch((err) => {
        setIsLoading(false);
        console.log(err);
      });
  };

  const parsedLeaveDays = useMemo(() => {
    try {
      const raw = rowData?.days;
      if (!raw) return [];
      if (Array.isArray(raw)) return raw;
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }, [rowData]);

  const renderApprovalState = (approval, approvalDate) => {
    if (approval == 10000) {
      return (
        <span className="approve-leave-status approve-leave-status--pending">
          <FaExclamationCircle /> Pending
        </span>
      );
    }
    if (approval == 10002) {
      return (
        <span className="approve-leave-status approve-leave-status--rejected">
          <FaTimesCircle /> Rejected
        </span>
      );
    }
    return (
      <span className="approve-leave-status approve-leave-status--approved">
        <FaCheckCircle /> Approved{approvalDate ? ` · ${approvalDate}` : ""}
      </span>
    );
  };

  const handleApprove = (level, roleLabel) => {
    if (!window.confirm(`Are you sure you want to approve this leave as ${roleLabel}?`)) {
      return;
    }
    approveLeave({
      request_id: rowData?.id,
      level,
      action: "10001",
      reason: approvalComments,
    });
  };

  const handleReject = (level, roleLabel) => {
    if (approvalComments.trim() === "") {
      alert("Please write a rejection comment");
      return;
    }
    if (!window.confirm(`Are you sure you want to reject this leave as ${roleLabel}?`)) {
      return;
    }
    approveLeave({
      request_id: rowData?.id,
      level,
      action: "10002",
      reason: approvalComments,
    });
  };

  return (
    <div className="approve-leave-page">
      {is_loading && !rowData && (
        <div className="approve-leave-loading">
          <PulseLoader color="#2563eb" size={14} />
          <p style={{ marginTop: "1.25rem" }}>Loading leave details…</p>
        </div>
      )}

      {rowData != null && (
        <div className="approve-leave-sheet">
          <div className="approve-leave-sheet__header">
            <div className="approve-leave-sheet__eyebrow">Leave approval</div>
            <h1 className="approve-leave-sheet__title">{leaveTypeName}</h1>
            <p className="approve-leave-sheet__subtitle">
              Request from <strong>{staffName}</strong>
              {rowData?.request_date ? (
                <>
                  {" "}
                  · <FaCalendarAlt style={{ marginBottom: "2px" }} /> Submitted {rowData.request_date}
                </>
              ) : null}
            </p>
          </div>

          <div className="approve-leave-sheet__body">
            <div className="approve-leave-fields">
              <Field label="Staff ID" value={rowData?.staff_id || "—"} />
              <Field label="Staff name" value={staffName} />
              <Field label="Email" value={rowData?.staff?.email || "—"} />
              <Field label="Total days" value={rowData?.total_days ?? 0} highlight />
            </div>

            {rowData?.leave_reason ? (
              <section className="approve-leave-section">
                <SectionTitle icon={FaCommentDots}>Leave reason</SectionTitle>
                <p className="approve-leave-reason">{rowData.leave_reason}</p>
              </section>
            ) : null}

            <section className="approve-leave-section">
              <div className="approve-leave-section__split">
                <div>
                  <SectionTitle icon={FaCalendarDay}>Requested days</SectionTitle>
                  {parsedLeaveDays.length ? (
                    <div className="approve-leave-days">
                      {parsedLeaveDays.map((d, i) => (
                        <span key={i} className="approve-leave-day-chip">
                          {d}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span style={{ color: "#64748b", fontSize: "1.1rem" }}>No leave days found.</span>
                  )}
                </div>
                <div>
                  <SectionTitle icon={FaUserFriends}>Handover</SectionTitle>
                  <div className="approve-leave-handover">
                    <div className="approve-leave-handover__avatar">
                      {getInitials(rowData?.handedover_to?.name)}
                    </div>
                    <div>
                      <div className="approve-leave-field__label">Handed over to</div>
                      <div className="approve-leave-handover__name">
                        {rowData?.handedover_to?.name || "—"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section className="approve-leave-section">
              <SectionTitle icon={FaFileAlt}>Supporting documents</SectionTitle>
              <LeaveDocumentList documents={leaveDocuments} />
            </section>

            <section className="approve-leave-section">
              <SectionTitle icon={FaClipboardCheck}>Approval status</SectionTitle>
              <div className="approve-leave-approval-row">
                <div className="approve-leave-approval-block">
                  <div className="approve-leave-approval-block__role">Supervisor</div>
                  {renderApprovalState(rowData?.supervisor_approval, rowData?.supervisor_approval_date)}
                  <div className="approve-leave-comment">
                    <strong>Comment:</strong> {rowData?.supervisor_comments || "—"}
                  </div>
                </div>
                <div className="approve-leave-approval-block">
                  <div className="approve-leave-approval-block__role">Human resources</div>
                  {renderApprovalState(rowData?.hr_approval, rowData?.hr_approval_date)}
                  <div className="approve-leave-comment">
                    <strong>Comment:</strong> {rowData?.hr_comments || "—"}
                  </div>
                </div>
              </div>
            </section>

            {canTakeAction ? (
              <section className="approve-leave-section">
                <label className="approve-leave-actions__label" htmlFor="approval-comment">
                  Your approval comment
                </label>
                <textarea
                  id="approval-comment"
                  className="approve-leave-textarea"
                  value={approvalComments}
                  onChange={(e) => setapprovalComments(e.target.value)}
                  rows={4}
                  placeholder="Add remarks (required for rejection)…"
                />
                {is_loading && (
                  <div style={{ textAlign: "center", marginTop: "1rem" }}>
                    <PulseLoader color="#2563eb" size={12} />
                  </div>
                )}
                <div className="approve-leave-btn-row">
                  {isSupervisorApprover && (
                    <>
                      <button
                        type="button"
                        className="approve-leave-btn approve-leave-btn--approve"
                        disabled={is_loading}
                        onClick={() => handleApprove(1, "Supervisor")}
                      >
                        <FaCheckCircle /> Approve as Supervisor
                      </button>
                      <button
                        type="button"
                        className="approve-leave-btn approve-leave-btn--reject"
                        disabled={is_loading}
                        onClick={() => handleReject(1, "Supervisor")}
                      >
                        <FaTimesCircle /> Reject as Supervisor
                      </button>
                    </>
                  )}
                  {isHrApprover && (
                    <>
                      <button
                        type="button"
                        className="approve-leave-btn approve-leave-btn--approve"
                        disabled={is_loading}
                        onClick={() => handleApprove(2, "HR")}
                      >
                        <FaCheckCircle /> Approve as HR
                      </button>
                      <button
                        type="button"
                        className="approve-leave-btn approve-leave-btn--reject"
                        disabled={is_loading}
                        onClick={() => handleReject(2, "HR")}
                      >
                        <FaTimesCircle /> Reject as HR
                      </button>
                    </>
                  )}
                </div>
              </section>
            ) : (
              <div className="approve-leave-view-only">
                <FaUser style={{ marginRight: "0.5rem", opacity: 0.5 }} />
                You are viewing this request. Approval actions are not assigned to your account.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ApproveLeave;
